import { GearItem, GearOwner, GearTarget, primaryValue, sameOwner } from "@highmarch/domain";
import { bestLoadout, compareOn, itemScore, outclassed } from "@highmarch/app";
import type { ArmouryCtx } from "./host";
import { openItemCard } from "./itemCard";
import { btn, emptyEl, faceEl, h, ico, isNew, newSince, ownerName, ownerViews, slotGhost } from "./itemBits";
import { openClearJunk } from "./junk";
import { gearTile } from "./tile";
import { essImg, sigilImg } from "./itemBits";
import { SIGIL_RARITIES } from "../forgeArt";
import { fuseBadge } from "./forgeModel";
import { openFusion } from "./fusion";

/**
 * The Stash tab body (mockup4): one control row (slot kind + sort), then the items by usefulness instead of by
 * filters — Upgrades (the best placements for the next level, then what would improve any owned owner, each
 * tagged with where it goes), Bench & spares (in the bag or on a bench tower), and Outclassed (items no owner would
 * ever pick; folded, with Clear junk). Items worn by the line-up are not here: they are on the doll.
 */

export type StashKind = "all" | "tower" | "base";
export type StashSort = "best" | "new" | "rarity";

export interface StashEntry {
  readonly item: GearItem;
  /** Where it would go: the owner it upgrades, or the bench tower whose empty socket it fits. */
  readonly to: GearOwner | null;
  /** Gear gained there. */
  readonly delta: number | null;
}
export interface StashModel {
  readonly upgrades: readonly StashEntry[];
  readonly spares: readonly StashEntry[];
  readonly outclassed: readonly StashEntry[];
  /** Items in the stash per kind chip (before the kind filter). */
  readonly counts: Readonly<Record<StashKind, number>>;
}

const SORT_LABEL: Record<StashSort, string> = { best: "Best", new: "Newest", rarity: "Rarity" };
const SORTS: readonly StashSort[] = ["best", "new", "rarity"];
const KINDS: readonly { key: StashKind; label: string }[] = [{ key: "all", label: "All" }, { key: "tower", label: "Towers" }, { key: "base", label: "Fortress" }];

/** Per Armoury sheet: the chosen chip, sort and whether Outclassed is open (kept across re-renders). */
const state = new WeakMap<HTMLElement, { kind: StashKind; sort: StashSort; outOpen: boolean }>();

/** The stash's sections for a kind chip and a sort (pure over the profile). */
export function stashModel(ctx: ArmouryCtx, kind: StashKind = "all", sort: StashSort = "best"): StashModel {
  const B = ctx.B, profile = ctx.profile;
  const views = ownerViews(ctx);
  const onSeat = (it: GearItem) => it.equippedTo !== null && views.some((v) => v.seat && sameOwner(v.owner, it.equippedTo!));
  const pool = profile.gear.filter((it) => !onSeat(it));
  const kindOf = (it: GearItem): StashKind => (B.gear.slots[it.slot].target === GearTarget.Base ? "base" : "tower");
  const counts = { all: pool.length, tower: pool.filter((it) => kindOf(it) === "tower").length, base: pool.filter((it) => kindOf(it) === "base").length };
  const shown = pool.filter((it) => kind === "all" || kindOf(it) === kind);
  const ids = new Set(shown.map((it) => it.id));

  // Upgrades: the best placements for the next level, then bag items that beat what any owned owner wears.
  // A bag item that would only fill an empty bench socket is a spare, tagged with that tower.
  const upgradeTo = new Map<number, GearOwner>(), benchTo = new Map<number, GearOwner>();
  for (const m of bestLoadout(profile, ctx.scope).moves) if (m.to !== null && ids.has(m.item.id)) upgradeTo.set(m.item.id, m.to);
  for (const m of bestLoadout(profile, { roster: ctx.scope.roster }).moves) {
    if (m.to === null || !ids.has(m.item.id) || m.item.equippedTo !== null || upgradeTo.has(m.item.id)) continue;
    (profile.equippedIn(m.to, m.item.slot) !== null ? upgradeTo : benchTo).set(m.item.id, m.to);
  }
  const junk = new Set(outclassed(profile, { roster: ctx.scope.roster, seenUpTo: newSince(ctx) }).map((it) => it.id));
  const order = sorter(ctx, sort);
  const entry = (item: GearItem): StashEntry => {
    const to = upgradeTo.get(item.id) ?? benchTo.get(item.id) ?? null;
    return { item, to, delta: to === null ? null : compareOn(profile, item, to).power };
  };
  const upgrades = shown.filter((it) => upgradeTo.has(it.id)).map(entry)
    .sort((a, b) => (sort === "best" ? (b.delta ?? 0) - (a.delta ?? 0) : 0) || order(a.item, b.item));
  const rest = shown.filter((it) => !upgradeTo.has(it.id));
  return {
    upgrades,
    spares: rest.filter((it) => benchTo.has(it.id) || !junk.has(it.id)).sort(order).map(entry),
    outclassed: rest.filter((it) => !benchTo.has(it.id) && junk.has(it.id)).sort(order).map(entry),
    counts,
  };
}

function sorter(ctx: ArmouryCtx, sort: StashSort): (a: GearItem, b: GearItem) => number {
  const B = ctx.B;
  if (sort === "new") return (a, b) => b.id - a.id;
  if (sort === "rarity") return (a, b) => b.rarity - a.rarity || b.level - a.level || b.id - a.id;
  return (a, b) => itemScore(B, b) - itemScore(B, a) || primaryValue(B, b).cmp(primaryValue(B, a)) || a.id - b.id;
}

/** Renders (or re-renders) the Stash tab into `container`. */
export function renderStash(container: HTMLElement, ctx: ArmouryCtx): void {
  const B = ctx.B;
  const st = state.get(ctx.sheet) ?? { kind: "all" as StashKind, sort: "best" as StashSort, outOpen: false };
  state.set(ctx.sheet, st);
  const m = stashModel(ctx, st.kind, st.sort);
  const again = () => renderStash(container, ctx);

  const chips = KINDS.map((k) => {
    const b = btn(`fchip${st.kind === k.key ? " on" : ""}`, k.label, h("small", "", String(m.counts[k.key])));
    b.addEventListener("click", () => { st.kind = k.key; again(); });
    return b;
  });
  const sortBtn = btn("fchip sort", SORT_LABEL[st.sort], ico("chev", 12));
  sortBtn.setAttribute("aria-label", `Sort: ${SORT_LABEL[st.sort]} (tap to change)`);
  sortBtn.addEventListener("click", () => { st.sort = SORTS[(SORTS.indexOf(st.sort) + 1) % SORTS.length]; again(); });
  const controls = h("div", "sfilt", ...chips, sortBtn);

  const tile = (e: StashEntry): HTMLElement => {
    const t = gearTile(ctx, e.item, { delta: e.delta, wornChip: e.item.equippedTo !== null && e.to === null, isNew: isNew(ctx, e.item), size: "m" });
    t.addEventListener("click", () => openItemCard(ctx, e.item, e.to === null ? undefined : { owner: e.to }));
    const wrap = h("div", "stile", t);
    wrap.dataset.id = String(e.item.id);
    if (e.to !== null) { const f = faceEl(B, e.to, "goes"); f.title = `Goes on ${ownerName(B, e.to)}`; wrap.append(f); }
    return wrap;
  };
  const grid = (xs: readonly StashEntry[]) => h("div", "sgrid", ...xs.map(tile));
  const head = (cls: string, title: string, note: string) => h("div", `gh ${cls}`, h("span", "", title), h("span", "ln"), h("span", "note", note));

  const list = h("div", "slist");
  if (m.counts[st.kind] === 0) {
    // The whole stash empty: the chest a win brings. One kind empty: that kind's four slots, ghosted.
    const kindSlots = B.gear.slots.filter((s) => (s.target === GearTarget.Base) === (st.kind === "base"));
    list.append(m.counts.all === 0 ? emptyEl("The stash is empty")
      : emptyEl(`No ${st.kind === "base" ? "fortress" : "tower"} gear in the stash`, h("span", "aghosts", ...kindSlots.map((s) => slotGhost(B, s.index, "sm")))));
  } else {
    // Upgrades only when there are some (no "nothing here" line); counts, not sentences.
    if (m.upgrades.length > 0) list.append(h("section", "ssec up", head("up", "Upgrades", String(m.upgrades.length)), grid(m.upgrades)));
    if (m.spares.length > 0) list.append(h("section", "ssec spare", head("", "Bench & spares", String(m.spares.length)), grid(m.spares)));
    if (m.outclassed.length > 0) {
      const toggle = btn(`gh out tog${st.outOpen ? " open" : ""}`, ico("chev", 12), h("span", "", "Outclassed"), h("span", "n", String(m.outclassed.length)), h("span", "ln"));
      toggle.setAttribute("aria-expanded", String(st.outOpen));
      toggle.addEventListener("click", () => { st.outOpen = !st.outOpen; again(); });
      const clear = btn("abtn gold sm junkbtn", h("span", "disp", "Clear junk"), ico("chev", 14));
      clear.addEventListener("click", () => openClearJunk(ctx));
      const sec = h("section", "ssec out", toggle, clear);
      if (st.outOpen) sec.append(grid(m.outclassed));
      list.append(sec);
    }
  }
  container.replaceChildren(controls, forgeBar(ctx), list);
}

/**
 * The Stash's Forge bar (gear v2): Forge Essence and the sigils held, and FUSE — gold with the count of fusable groups
 * (the FUSE badge, forgeView `fusableGroups`) when three of a rarity can become one, else the plain way in.
 */
export function forgeBar(ctx: ArmouryCtx): HTMLElement {
  const p = ctx.profile;
  const purse = h("div", "purse", h("span", "pe", essImg(), h("b", "", String(p.forge.essence))));
  for (const r of SIGIL_RARITIES) {
    const n = p.forge.sigils[r] ?? 0;
    if (n > 0) purse.append(h("span", `ps r${r}`, sigilImg(r), h("b", "", String(n))));
  }
  const { count } = fuseBadge(p);
  const fuse = btn(`abtn ${count > 0 ? "gold" : "parch"} sm fusebtn`, h("span", "disp", "FUSE"), count > 0 ? h("i", "dot", String(count)) : null);
  fuse.setAttribute("aria-label", count > 0 ? `Fuse: ${count} group${count === 1 ? "" : "s"} ready` : "Fusion");
  fuse.addEventListener("click", () => openFusion(ctx, { openItem: (it) => openItemCard(ctx, it) }));
  return h("div", "sforge", h("span", "lbl", "FORGE"), purse, fuse);
}
